// src/components/UiFeedbackWidget/elementIdentity.js
//
// Spec 20260925-ui-feedback §6.3 (UD-NO-CONTENT), plan §5.6.1 / §5.6.2. Tasks T044.
//
// ═══ THE data-kb-id CONVENTION — READ BEFORE ANNOTATING OR RENAMING ═══════════════════
//
//   <pageId>.<zone>            e.g. devices.filters, alarms-dashboard.table
//   <pageId>.action.<x>        e.g. devices.action.ping  (the existing KB action ids)
//
//   pageId  = the PAGE_REGISTRY id of the page (@caro/shared/constants), NOT the URL:
//             /alarms is `alarms-dashboard`.
//   zone    = one word from this fixed list, so the chip can name it in six languages:
//             page-header · filters · table · list · tabs · detail · toolbar
//   Lower case, digits, dots and hyphens only: ^[a-z0-9][a-z0-9.-]{0,79}$.
//
// ⚠ The knowledge base (tools/kb-generator) and UI feedback share these ids. A renamed
//   data-kb-id breaks the knowledge base AND silently re-labels every past feedback item
//   that points at it. Rename only on purpose, and never reuse an id for another zone.
//
// ═══ WHAT GOES OUT — IDENTITY, NEVER CONTENT ════════════════════════════════════════
//
//   elementPath  every data-kb-id from the picked element up to <body>, outside → inside,
//                the innermost five
//   elementKbId  the innermost of those, or null
//   elementRole  the ARIA role attribute, else a role derived from the tag, else 'element'
//   elementLabel ONLY when ALL of these hold:
//                  - the role is button, tab or menuitem;
//                  - the element is not inside table, [role=grid], [role=list], li,
//                    [role=row] or [role=listbox] — rows and lists carry customer data,
//                    on their buttons too ("Bel Mevrouw Janssens");
//                  - the label is aria-label, or the element's OWN text nodes (never the
//                    text of a child element), and it is at most 60 characters.
//                Otherwise null.
//   Never `value`, innerHTML, or any attribute other than role / aria-label / data-kb-id.
//   The server additionally PII-scans the label and drops it on a hit.

const MAX_PATH = 5;
const LABEL_MAX = 60;
const KB_ID_RE = /^[a-z0-9][a-z0-9.-]{0,79}$/;

export const ZONES = Object.freeze(['page-header', 'filters', 'table', 'list', 'tabs', 'detail', 'toolbar']);

/** Roles the chip can name; anything else shows "Element". */
export const NAMED_ROLES = Object.freeze([
  'button',
  'link',
  'tab',
  'menuitem',
  'cell',
  'columnheader',
  'row',
  'textbox',
  'table',
  'list',
  'listitem',
  'heading',
  'toolbar',
]);

const LABELLED_ROLES = ['button', 'tab', 'menuitem'];
const CONTENT_CONTAINERS = 'table,[role=grid],[role=list],li,[role=row],[role=listbox]';

const TAG_ROLE = {
  button: 'button',
  a: 'link',
  th: 'columnheader',
  td: 'cell',
  tr: 'row',
  table: 'table',
  textarea: 'textbox',
  select: 'combobox',
  ul: 'list',
  ol: 'list',
  li: 'listitem',
  nav: 'navigation',
  header: 'banner',
  img: 'img',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  h5: 'heading',
  h6: 'heading',
  form: 'form',
};

const INPUT_ROLE = {
  checkbox: 'checkbox',
  radio: 'radio',
  button: 'button',
  submit: 'button',
  reset: 'button',
  range: 'slider',
};

const EMPTY = Object.freeze({ elementKbId: null, elementPath: [], elementRole: null, elementLabel: null });

function roleOf(el) {
  const attr = el.getAttribute('role');
  if (attr && attr.trim()) return attr.trim().split(/\s+/)[0].slice(0, 40);
  const tag = el.tagName.toLowerCase();
  if (tag === 'input') return INPUT_ROLE[(el.getAttribute('type') || '').toLowerCase()] || 'textbox';
  return TAG_ROLE[tag] || 'element';
}

function ownText(el) {
  let text = '';
  for (const node of el.childNodes) {
    if (node.nodeType === 3) text += node.nodeValue; // TEXT_NODE only
  }
  return text.replace(/\s+/g, ' ').trim();
}

function labelOf(el, role) {
  if (!LABELLED_ROLES.includes(role)) return null;
  if (el.closest(CONTENT_CONTAINERS)) return null;
  const aria = el.getAttribute('aria-label');
  const raw = aria && aria.trim() ? aria.replace(/\s+/g, ' ').trim() : ownText(el);
  if (!raw || raw.length > LABEL_MAX) return null;
  return raw;
}

/**
 * The identity of a picked element. Pure over the DOM; never throws.
 * @param {Element|null} target
 */
export function elementIdentity(target) {
  if (!target || typeof target !== 'object' || target.nodeType !== 1) return { ...EMPTY, elementPath: [] };

  const path = [];
  for (let el = target; el && el.nodeType === 1 && el !== document.body; el = el.parentElement) {
    const id = el.getAttribute('data-kb-id');
    if (id && KB_ID_RE.test(id)) path.unshift(id);
  }
  const elementPath = path.slice(-MAX_PATH);
  const elementRole = roleOf(target);

  return {
    elementKbId: elementPath.length ? elementPath[elementPath.length - 1] : null,
    elementPath,
    elementRole,
    elementLabel: labelOf(target, elementRole),
  };
}

function humanize(x) {
  const words = String(x).replace(/[.-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * The human name on the element chip (plan §5.6.2). There is no kb-id → name registry;
 * the fixed zone vocabulary is what makes a translated name possible.
 *
 *   `*.action.<x>`    → t('Ideas.uiFeedback.action_label', { x: 'Ping' })
 *   `<page>.<zone>`   → t('Ideas.uiFeedback.zone.<zone>')
 *   otherwise         → t('Ideas.uiFeedback.role.<role>') for a known role, else "Element"
 */
export function displayNameFor(kbId, role, t) {
  if (typeof kbId === 'string' && kbId) {
    const segs = kbId.split('.');
    const a = segs.indexOf('action');
    if (a >= 0 && a < segs.length - 1) {
      return t('Ideas.uiFeedback.action_label', { x: humanize(segs.slice(a + 1).join(' ')) });
    }
    const zone = segs[segs.length - 1];
    if (ZONES.includes(zone)) return t(`Ideas.uiFeedback.zone.${zone}`);
  }
  if (role && NAMED_ROLES.includes(role)) return t(`Ideas.uiFeedback.role.${role}`);
  return t('Ideas.uiFeedback.element');
}
